// src/components1/BottomNav.js
import React, { useState, useEffect, useRef, useCallback } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import "../styles/BottomNav.css";

const BottomNav = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const navItemsRef = useRef(null);
  const tabRefs = useRef([]);
  const hasMountedRef = useRef(false);

  const [indicatorStyle, setIndicatorStyle] = useState({
    opacity: 0,
    transform: "translate3d(0px, 0px, 0)",
    transition: "none",
  });

  const getActiveIndex = useCallback(() => {
    const p = location.pathname;
    if (p.includes("/app1/home") || p === "/") return 0;
    if (p.includes("/app1/sharedexpense")) return 1;
    if (p.includes("/app1/todo")) return 2;
    if (p.includes("/app1/settings")) return 3;
    return -1;
  }, [location.pathname]);

  const updateIndicatorPosition = useCallback((targetIndex = null) => {
    const idx = targetIndex !== null ? targetIndex : getActiveIndex();
    if (idx < 0 || !tabRefs.current[idx] || !navItemsRef.current) {
      setIndicatorStyle((prev) => ({ ...prev, opacity: 0 }));
      return;
    }

    const containerRect = navItemsRef.current.getBoundingClientRect();
    const tabEl = tabRefs.current[idx];
    const tabRect = tabEl.getBoundingClientRect();
    const iconEl = tabEl.querySelector(".bottom-nav-icon") || tabEl.querySelector("span");
    const iconRect = iconEl ? iconEl.getBoundingClientRect() : null;

    const barWidth = 22;
    const barHeight = 3;
    const gapAboveIcon = 3;

    const x = tabRect.left - containerRect.left + (tabRect.width / 2) - (barWidth / 2);
    const top = iconRect
      ? (iconRect.top - containerRect.top - gapAboveIcon - barHeight)
      : (tabRect.top - containerRect.top + 8);

    const isFirst = !hasMountedRef.current;
    if (isFirst) {
      hasMountedRef.current = true;
    }

    setIndicatorStyle({
      opacity: 1,
      transform: `translate3d(${Math.round(x)}px, ${Math.round(top)}px, 0)`,
      transition: isFirst
        ? "opacity 0.2s ease"
        : "transform 0.32s cubic-bezier(0.25, 1, 0.5, 1), opacity 0.2s ease",
    });
  }, [getActiveIndex]);

  useEffect(() => {
    updateIndicatorPosition();
  }, [location.pathname, updateIndicatorPosition]);

  useEffect(() => {
    const handleResize = () => updateIndicatorPosition();
    window.addEventListener("resize", handleResize);
    window.addEventListener("orientationchange", handleResize);
    const mql = window.matchMedia ? window.matchMedia("(orientation: landscape)") : null;
    if (mql && mql.addEventListener) {
      mql.addEventListener("change", handleResize);
    }
    const t = setTimeout(handleResize, 100);
    const t2 = setTimeout(handleResize, 300);
    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("orientationchange", handleResize);
      if (mql && mql.removeEventListener) {
        mql.removeEventListener("change", handleResize);
      }
      clearTimeout(t);
      clearTimeout(t2);
    };
  }, [updateIndicatorPosition]);

  const handleFabClick = () => {
    const isGuest = localStorage.getItem("loggedInUserEmail") === "guest" || !localStorage.getItem("loggedInUser");
    if (location.pathname.includes("/app1/savings")) {
      window.dispatchEvent(new CustomEvent("open-add-saving"));
    } else if (location.pathname.includes("/app1/todo")) {
      window.dispatchEvent(new CustomEvent("open-add-todo"));
    } else if (location.pathname.includes("/app1/sharedexpense") && isGuest) {
      navigate("/app1/home");
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent("open-add-expense"));
      }, 100);
    } else if (!location.pathname.includes("/app1/home") && !location.pathname.includes("/app1/sharedexpense")) {
      navigate("/app1/home");
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent("open-add-expense"));
      }, 100);
    } else {
      window.dispatchEvent(new CustomEvent("open-add-expense"));
    }
  };

  return (
    <div className="bottom-nav-container">
      <nav className="bottom-nav-dock">
        {/* SVG curved notch cutout background with light glass lavender gradient */}
        <svg className="bottom-nav-svg-bg" viewBox="0 0 400 64" preserveAspectRatio="none">
          <defs>
            <linearGradient id="dockGlassGrad" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#EDE2F2" stopOpacity="0.80" />
              <stop offset="100%" stopColor="#DCCDE5" stopOpacity="0.68" />
            </linearGradient>
          </defs>
          <path
            d="M 24,0 
               L 376,0 
               A 24,24 0 0 1 400,24 
               L 400,64 
               L 0,64 
               L 0,24 
               A 24,24 0 0 1 24,0 
               Z"
            fill="url(#dockGlassGrad)"
            stroke="rgba(255, 255, 255, 0.65)"
            strokeWidth="1.2"
            vectorEffect="non-scaling-stroke"
          />
        </svg>

        <div className="bottom-nav-items" ref={navItemsRef}>
          {/* Smoothly Sliding Active Indicator Bar */}
          <div
            className="bottom-nav-sliding-indicator"
            style={indicatorStyle}
            aria-hidden="true"
          />

          {/* Tab 1: Home */}
          <NavLink
            ref={(el) => (tabRefs.current[0] = el)}
            to="/app1/home"
            className={({ isActive }) =>
              `bottom-nav-tab ${isActive ? "active" : ""}`
            }
            onClick={() => updateIndicatorPosition(0)}
          >
            <div className="bottom-nav-tab-spacer" aria-hidden="true" />
            <span className="bottom-nav-icon">
              <i className="fi fi-rr-home"></i>
            </span>
            <div className="bottom-nav-label">Home</div>
          </NavLink>

          {/* Tab 2: Shared Expenses */}
          <NavLink
            ref={(el) => (tabRefs.current[1] = el)}
            to="/app1/sharedexpense"
            className={({ isActive }) => `bottom-nav-tab ${isActive ? "active" : ""}`}
            onClick={() => updateIndicatorPosition(1)}
          >
            <div className="bottom-nav-tab-spacer" aria-hidden="true" />
            <span className="bottom-nav-icon">
              <i className="fi fi-rr-users-alt"></i>
            </span>
            <div className="bottom-nav-label">Shared</div>
          </NavLink>

          {/* Center Notch: Floating Action Button */}
          <div className="bottom-nav-fab-wrap">
            <button
              key={location.pathname}
              className={`bottom-nav-fab ${location.pathname.includes("/app1/savings") ? "is-savings" : ""}`}
              onClick={handleFabClick}
              title={location.pathname.includes("/app1/savings") ? "Add Saving Account" : "Add New"}
              aria-label={location.pathname.includes("/app1/savings") ? "Add Saving Account" : "Add New"}
            >
              <div className="bottom-nav-fab-gloss" aria-hidden="true" />
              <div className="bottom-nav-fab-inner">
                <i className="fi fi-sr-plus"></i>
              </div>
            </button>
          </div>

          {/* Tab 3: To Do */}
          <NavLink
            ref={(el) => (tabRefs.current[2] = el)}
            to="/app1/todo"
            className={({ isActive }) => `bottom-nav-tab ${isActive ? "active" : ""}`}
            onClick={() => updateIndicatorPosition(2)}
          >
            <div className="bottom-nav-tab-spacer" aria-hidden="true" />
            <span className="bottom-nav-icon">
              <i className="fi fi-rr-checkbox"></i>
            </span>
            <div className="bottom-nav-label">To Do</div>
          </NavLink>

          {/* Tab 4: Settings */}
          <NavLink
            ref={(el) => (tabRefs.current[3] = el)}
            to="/app1/settings"
            className={({ isActive }) => `bottom-nav-tab ${isActive ? "active" : ""}`}
            onClick={() => updateIndicatorPosition(3)}
          >
            <div className="bottom-nav-tab-spacer" aria-hidden="true" />
            <span className="bottom-nav-icon">
              <i className="fi fi-rr-settings"></i>
            </span>
            <div className="bottom-nav-label">Settings</div>
          </NavLink>
        </div>
      </nav>
    </div>
  );
};

export default BottomNav;

